import { useState } from 'react';
import { ServiceType } from '@prisma/client';
import { toast } from 'react-hot-toast';

interface ServiceFormData {
  name: string;
  email: string;
  phone?: string;
  message?: string;
  serviceType: ServiceType;
  location?: string;
  propertySize?: string;
  preferredDate?: Date | string;
  specificService?: string;
  [key: string]: any; // Allow for additional service-specific fields
}

interface ServiceFormResponse {
  success: boolean;
  message: string;
  bookingId?: string;
  error?: string;
}

export const useServiceForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to check and adjust dates
  const validateAndAdjustDate = (inputDate: Date | string): Date => {
    let date = new Date();

    // Parse input date if it's a string
    if (typeof inputDate === 'string') {
      date = new Date(inputDate);
    } else if (inputDate instanceof Date) {
      date = new Date(inputDate.getTime()); // Create a copy of the date
    }

    // Ensure date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    if (date < today) {
      // If date is in the past, set it to today
      date = new Date();
      console.warn('Booking date was in the past, adjusted to today');
    }

    // Check if date is a weekend
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0) {
      // Sunday
      // Move to next Monday (add 1 day)
      date.setDate(date.getDate() + 1);
      console.warn('Booking date was on Sunday, adjusted to Monday');
    } else if (dayOfWeek === 6) {
      // Saturday
      // Move to next Monday (add 2 days)
      date.setDate(date.getDate() + 2);
      console.warn('Booking date was on Saturday, adjusted to Monday');
    }

    return date;
  };

  const submitServiceRequest = async (
    formData: ServiceFormData
  ): Promise<ServiceFormResponse> => {
    // Prevent duplicate submissions
    if (isSubmitting) {
      return {
        success: false,
        message: 'Request already being processed',
        error: 'A submission is already in progress',
      };
    }

    setIsSubmitting(true);
    const toastId = toast.loading('Submitting your request...');

    try {
      // Create a copy of the form data to avoid mutating the original
      const processedData = { ...formData };

      // Validate and adjust the preferred date if provided
      if (processedData.preferredDate) {
        const adjustedDate = validateAndAdjustDate(processedData.preferredDate);
        processedData.preferredDate = adjustedDate.toISOString();

        // Inform the user if their date was adjusted
        const originalDate =
          typeof formData.preferredDate === 'string'
            ? new Date(formData.preferredDate)
            : formData.preferredDate;

        const originalDateStr =
          originalDate instanceof Date
            ? originalDate.toDateString()
            : 'Invalid date';
        const adjustedDateStr = adjustedDate.toDateString();

        if (originalDateStr !== adjustedDateStr) {
          // Use the same toastId to replace the loading toast
          toast.success(
            `Your booking has been scheduled for ${adjustedDateStr} as service booking can only be effected on weekdays (Monday-Friday).`,
            { id: toastId }
          );
        }
      }

      // Define color schemes for each service type
      const serviceColors = {
        [ServiceType.SURVEY]: {
          primary: 'blue',
          secondary: 'teal',
          bg: 'blue-900/20',
          border: 'blue-800/50',
          text: 'blue-200',
          hover: 'blue-600',
          focus: 'blue-500/20',
        },
        [ServiceType.GIS]: {
          primary: 'emerald',
          secondary: 'green',
          bg: 'emerald-900/20',
          border: 'emerald-800/50',
          text: 'emerald-200',
          hover: 'emerald-600',
          focus: 'emerald-500/20',
        },
        [ServiceType.PROPERTY_MANAGEMENT]: {
          primary: 'purple',
          secondary: 'violet',
          bg: 'purple-900/20',
          border: 'purple-800/50',
          text: 'purple-200',
          hover: 'purple-600',
          focus: 'purple-500/20',
        },
        [ServiceType.CYBER]: {
          primary: 'indigo',
          secondary: 'blue',
          bg: 'indigo-900/20',
          border: 'indigo-800/50',
          text: 'indigo-200',
          hover: 'indigo-600',
          focus: 'indigo-500/20',
        },
        [ServiceType.GAS_SUPPLY]: {
          primary: 'orange',
          secondary: 'amber',
          bg: 'orange-900/20',
          border: 'orange-800/50',
          text: 'orange-200',
          hover: 'orange-600',
          focus: 'orange-500/20',
        },
        [ServiceType.PRINTING]: {
          primary: 'amber',
          secondary: 'orange',
          bg: 'amber-900/20',
          border: 'amber-800/50',
          text: 'amber-200',
          hover: 'amber-600',
          focus: 'amber-500/20',
        },
        [ServiceType.GENERAL_INQUIRY]: {
          primary: 'gray',
          secondary: 'slate',
          bg: 'gray-900/20',
          border: 'gray-800/50',
          text: 'gray-200',
          hover: 'gray-600',
          focus: 'gray-500/20',
        },
      };

      // Add color theme based on service type
      let serviceColorName = 'blue'; // Default color if not matched
      let serviceColorScheme = null;

      if (processedData.serviceType in serviceColors) {
        serviceColorScheme =
          serviceColors[processedData.serviceType as ServiceType];
        serviceColorName = serviceColorScheme.primary;
      }

      // Always set the service color to ensure forms maintain styling
      processedData.serviceColor = serviceColorName;

      // Add the full color scheme if available
      if (serviceColorScheme) {
        processedData.serviceColorScheme = serviceColorScheme;
      }

      // Submit the form data to our service API
      const response = await fetch('/api/services', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(processedData),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to submit service request';

        try {
          // Check the content type of the response
          const contentType = response.headers.get('content-type');

          if (contentType && contentType.includes('application/json')) {
            // Try to parse the error as JSON
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
            console.error('Service request error:', errorData);
          } else {
            // If not JSON, try to get the text
            const errorText = await response.text();
            console.error('Service request error (text):', errorText);

            // If the error seems to be HTML (might be from auth system)
            if (errorText.includes('<!DOCTYPE html>')) {
              errorMessage = 'Authentication error. Please try again.';
            }
          }
        } catch (e) {
          console.error('Could not parse error response:', e);
        }

        throw new Error(errorMessage);
      }

      // Try to parse the response as JSON
      let result;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        // If not JSON, try to get the text format and handle it gracefully
        const text = await response.text();
        console.warn('Unexpected response format:', text);

        // Try to extract useful information from text if possible
        result = {
          success: true,
          message: 'Service request submitted successfully',
          booking: { id: 'unknown' },
        };
      }

      // Update the loading toast to success (using the same ID prevents duplicate toasts)
      toast.success('Request submitted successfully!', { id: toastId });

      return {
        success: true,
        message: 'Service request submitted successfully',
        bookingId: result.booking?.id,
      };
    } catch (error) {
      console.error('Service form submission error:', error);
      // Update the loading toast to error (using the same ID prevents duplicate toasts)
      toast.error(
        error instanceof Error ? error.message : 'Failed to submit request',
        { id: toastId }
      );
      return {
        success: false,
        message: 'Failed to submit service request',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    submitServiceRequest,
  };
};
